import React, {useContext, useState} from 'react';
import TextForm from './TextForm';
import FieldConfiguration from '../../../models/form/FieldConfiguration';
import {LanguageId} from '../../../models/utils/Languages';
import UIListBox from '../../../ui/form/UIListBox';
import TextAreaForm from './TextAreaForm';
import {FieldType} from '../../../models/form/Field';
import DropdownForm from './DropdownForm';
import DropdownField from '../../../models/form/DropdownField';
import {IOption} from '../../../ui/form/UIOption';
import CheckboxForm from './CheckboxForm';
import PercentRatingForm from './PercentRatingForm';
import StarRatingForm from './StarRatingForm';
import RatingForm from './RatingForm';
import {ActionType, FormType, FormTypeContext} from '../types';
import {useParams} from 'react-router';
import {getFieldConfiguration} from '../Sections';
import SectionConfiguration from '../../../models/form/SectionConfiguration';
import Tooltip from '../../../ui/Tooltip';
import IconInfo from '../../icon/IconInfo';

type Props = {
  defaultLanguage: LanguageId
  sections: SectionConfiguration[]
  onCreateUpdate: (action: ActionType, el?: FieldConfiguration) => void
}

interface FieldParams {
  id?: string
}

export default function SideFieldForm({
  defaultLanguage, sections, onCreateUpdate,
}: Props) {

  const {id} = useParams<FieldParams>();

  const sortedSections =
    sections
      .sort((a, b) => a.getLabel(defaultLanguage).localeCompare(b.getLabel(defaultLanguage)))

  const formTypeContext = useContext(FormTypeContext);
  const field = getFieldConfiguration(id || 'new', sections);
  const title = field ? `Edit ${formTypeContext.token}` : `Add a new ${formTypeContext.token}`;
  const [fieldType, setFieldType] = useState(field?.field.type || FieldType.Text);
  const [sectionId, setSectionId] = useState<string | undefined>(field?.field.sectionId)

  const onChange = (value: FieldType) => {
    setFieldType(value);
  };

  // Users cannot change the type of Dropdown field
  const disabled = DropdownField.isDropdown(field?.field);

  const makeOption = (label: string, type: FieldType): IOption<boolean> => ({
    label,
    value: type,
    selected: false,
    id: type,
    disabled: field?.field !== undefined && !field?.field.canConvertTo(type),
  });

  const options: IOption<boolean>[] = [
    makeOption('Checkbox', FieldType.Checkbox),
    makeOption('Dropdown', FieldType.Dropdown),
    makeOption(`Long ${formTypeContext.type === FormType.RegistrationForm ? 'text' : 'answer'}`, FieldType.TextArea),
    makeOption('Percents / NPS', FieldType.PercentRating),
    makeOption('Rating', FieldType.Rating),
    makeOption(`Short ${formTypeContext.type === FormType.RegistrationForm ? 'text' : 'answer'}`, FieldType.Text),
    makeOption('Star rating', FieldType.StarRating),
  ].filter((x) => !x.disabled);

  const sectionsAsOptions: IOption<boolean>[] =
    sortedSections
      .map((x) => ({
        label: x.getLabel(defaultLanguage),
        value: x.id,
        selected: false,
        id: x.id,
        disabled: false
      }));

  return (
    <>
      <h2 className="font-bold text-2xl pb-3 flex justify-between">
        <span>{title}</span>
      </h2>
      <div>
        <label className="block">Assigned to section
          <Tooltip
            content={<div className="w-36">Defines under what section the {formTypeContext.token} is visible in
                                           the list of all {formTypeContext.token}s and sections</div>}
            label="assign_to_section" className="pl-0.5">
            <IconInfo className="text-gray-dark "/>
          </Tooltip>
        </label>
        <UIListBox onChange={(newSectionId) => setSectionId(newSectionId)} options={sectionsAsOptions}
          value={sectionId}
          className="mt-1 pb-3"/>
      </div>
      <div>
        <label className="block">{formTypeContext.capitalizeToken()} type</label>
        <UIListBox onChange={onChange} options={options} value={fieldType} className="mt-1 pb-3"
          disabled={disabled}/>
      </div>
      {fieldType === FieldType.Checkbox && <CheckboxForm sectionId={sectionId}
        configuration={field} defaultLanguage={defaultLanguage}
        onCreateUpdate={onCreateUpdate}/>}
      {fieldType === FieldType.Dropdown && <DropdownForm sectionId={sectionId}
        configuration={field} defaultLanguage={defaultLanguage}
        onCreateUpdate={onCreateUpdate}/>}
      {fieldType === FieldType.PercentRating && <PercentRatingForm sectionId={sectionId}
        configuration={field} defaultLanguage={defaultLanguage}
        onCreateUpdate={onCreateUpdate}/>}
      {fieldType === FieldType.Rating && <RatingForm sectionId={sectionId}
        configuration={field} defaultLanguage={defaultLanguage}
        onCreateUpdate={onCreateUpdate}/>}
      {fieldType === FieldType.StarRating && <StarRatingForm sectionId={sectionId}
        configuration={field} defaultLanguage={defaultLanguage}
        onCreateUpdate={onCreateUpdate}/>}
      {fieldType === FieldType.Text && <TextForm sectionId={sectionId}
        configuration={field} defaultLanguage={defaultLanguage}
        onCreateUpdate={onCreateUpdate}/>}
      {fieldType === FieldType.TextArea && <TextAreaForm sectionId={sectionId}
        configuration={field} defaultLanguage={defaultLanguage}
        onCreateUpdate={onCreateUpdate}/>}
    </>
  );
}
